import { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Upload, ShieldCheck, ListFilter, Loader2, ChevronDown, Tag, Undo2, Sparkles, FlaskConical } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface UploadConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (addCustomLabel: boolean) => void;
  approvedCount: number;
  /** Dashboard filters / checkbox selection narrow this upload (counts are scoped). */
  scoped?: boolean;
  /** Scoped counts are still being fetched — show placeholders, block confirm. */
  countsLoading?: boolean;
  /**
   * Der Feed erfuellt die Voraussetzungen fuer einen A/B-Test (oder wuerde sie
   * nach vollstaendiger Freigabe erfuellen). Dann ist dieser Upload eine Gabel:
   * `experiment_eligibility` verlangt `upload_status = 'not_uploaded'`, ein
   * vollstaendiger Upload nimmt dem Feed also die Vergleichsgruppe. Nur ein
   * Hinweis, keine Sperre.
   */
  experimentAtRisk?: boolean;
  /**
   * Der Upload-Request laeuft bereits (useUploadJob.uploadSubmitting). Sperrt
   * den Bestaetigungsknopf ab dem ersten Klick — Radix haelt den Dialog fuer
   * die 200-ms-Ausblend-Animation gemountet und klickbar, ein Doppelklick
   * startete sonst zwei Upload-Jobs, von denen "Abbrechen" nur den zweiten
   * erreichte. Zweite Haelfte des Schutzes ist der In-Flight-Ref in
   * useUploadJob.handleUpload (der State hier greift erst nach dem naechsten
   * Render).
   */
  submitting?: boolean;
}

/**
 * Bestaetigung vor dem Upload zu Google.
 *
 * TASK 11: der Alles-hochladen-Pfad hinter der Risiko-Checkbox ist weg. Er war
 * die Fluchttuer aus der Pflicht-Freigabe: zwei Klicks, und ungeprueft
 * generierte Texte standen im Umsatzkanal. Seit `enqueue-upload-batches` fuer
 * jeden Browser-Aufruf `approval_status='approved'` verlangt, waere die Option
 * ausserdem eine Zahl ohne Deckung gewesen: der Server haette sie ignoriert.
 *
 * Dafuer traegt dieser Dialog jetzt zwei Zusagen, die vorher an
 * `UploadAllButton`/`SafetyHintDialog` hingen, also an der Flaeche, die der
 * Hauptpfad-Schnitt aufgeloest hat:
 *
 *  - N2, Wiederherstellbarkeit: die eigentliche Angst ist nicht der schlechte
 *    Titel, sondern "etwas kaputtmachen, das ich nicht zurueckdrehen kann".
 *    Ohne diesen Block loeschte der Umbau die Risiko-Entschaerfung genau an dem
 *    Moment, an dem die Pflicht-Kette zum ersten Mal auf den Umsatzkanal
 *    schreibt.
 *  - N3, KI-Deklaration: `process-upload-queue` meldet die optimierten Texte
 *    ueber `structuredTitle`/`structuredDescription` mit
 *    `digitalSourceType: 'TRAINED_ALGORITHMIC_MEDIA'` an Google, genau wie
 *    Google es verlangt (am Quelltext gegengeprueft). Nicht KI-Content ist das
 *    Risiko, sondern nicht deklarierter. Gesagt wurde es bisher in keiner
 *    einzigen Zeile Copy.
 */
export function UploadConfirmDialog({
  open,
  onClose,
  onConfirm,
  approvedCount,
  scoped = false,
  countsLoading = false,
  experimentAtRisk = false,
  submitting = false,
}: UploadConfirmDialogProps) {
  const { t } = useTranslation('uploadConfirm');
  const [addCustomLabel, setAddCustomLabel] = useState(true);
  const [howToOpen, setHowToOpen] = useState(false);

  useEffect(() => {
    if (open && !countsLoading) {
      setAddCustomLabel(true);
      setHowToOpen(false);
    }
  }, [open, countsLoading]);

  const handleOpenChange = (v: boolean) => {
    if (!v) onClose();
  };

  const countLabel = (n: number) => (countsLoading ? '…' : n.toLocaleString());

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            {t('title')}
          </DialogTitle>
          <DialogDescription>{t('subtitle')}</DialogDescription>
        </DialogHeader>

        {/* Scope hint — dashboard filters / checkbox selection narrow this upload */}
        {scoped && (
          <div className="flex items-start gap-2 rounded-lg border border-primary/20 bg-accent p-3">
            {countsLoading ? (
              <Loader2 className="h-4 w-4 mt-0.5 shrink-0 animate-spin text-primary" />
            ) : (
              <ListFilter className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
            )}
            <p className="text-xs text-primary-ink leading-relaxed">
              {countsLoading ? t('scopedCounting') : t('scopedHint')}
            </p>
          </div>
        )}

        {/* Was hochgeht. Keine Auswahl mehr, sondern eine Aussage: nur
            Freigegebenes verlaesst die Werkbank. */}
        <div className="flex items-start gap-3 rounded-lg border border-success bg-success-light p-4">
          <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-success-ink" />
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{t('approvedOnly.label')}</span>
              <span className="ml-auto text-xs font-semibold text-success-ink bg-success/15 px-2 py-0.5 rounded-full">
                {/* `count` numerisch fuer die Pluralwahl, `formatted` fuer die
                    Anzeige (waehrend countsLoading ein Ellipsis-Platzhalter).
                    Ein String-count schaltet die i18next-Pluralauswahl ab —
                    daher stand hier "1 Produkte". */}
                {t('productsBadge', { count: approvedCount, formatted: countLabel(approvedCount) })}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">{t('approvedOnly.description')}</p>
          </div>
        </div>

        {/* Custom label opt-out — always visible, default checked */}
        <div className="space-y-1.5">
          <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-3">
            <Checkbox
              id="add-custom-label"
              checked={addCustomLabel}
              onCheckedChange={(v) => setAddCustomLabel(!!v)}
              className="mt-0.5"
            />
            <Label htmlFor="add-custom-label" className="text-xs text-foreground leading-relaxed cursor-pointer flex items-start gap-1.5">
              <Tag className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
              {t('customLabel.checkboxLabel')}
            </Label>
          </div>

          <Collapsible open={howToOpen} onOpenChange={setHowToOpen}>
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-1.5 pl-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${howToOpen ? 'rotate-180' : ''}`} />
                {t('customLabel.howToTitle')}
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <p className="mt-1.5 pl-1 pr-1 text-xs text-muted-foreground leading-relaxed">
                {t('customLabel.howToBody')}
              </p>
            </CollapsibleContent>
          </Collapsible>

          {!addCustomLabel && (
            <p className="pl-1 text-xs text-muted-foreground">{t('customLabel.skippedNote')}</p>
          )}
        </div>

        {/* N2: die Wiederherstellbarkeits-Zusage, umgezogen aus dem
            Sicherheits-Tooltip des aufgeloesten UploadAllButton. */}
        <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-3">
          <Undo2 className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
          <div className="space-y-1">
            <p className="text-xs font-medium text-foreground">{t('revert.title')}</p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              <Trans
                t={t}
                i18nKey="revert.body"
                components={{
                  // `<link>` ist ein HTML-Void-Element (siehe `void-elements`,
                  // das `html-parse-stringify` unter Trans nutzt) und wird vom
                  // Parser sofort selbst geschlossen -- der Linktext landet
                  // dann als Fliesstext NEBEN einem leeren <a>, nicht darin
                  // (Task 11, Befund 1). Tag-Name muss zum Locale-String
                  // passen (`<settingsLink>` in uploadConfirm.json).
                  settingsLink: (
                    <Link
                      to="/settings?tab=feeds"
                      className="underline font-medium text-primary hover:text-primary/80"
                    />
                  ),
                }}
              />
            </p>
          </div>
        </div>

        {/* N3: die KI-Deklaration. Der staerkste verfuegbare Vertrauensbeleg,
            und er stand bisher in keiner Zeile Copy. */}
        <p className="flex items-start gap-2 pl-1 text-xs text-muted-foreground leading-relaxed">
          <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
          {t('aiDeclaration')}
        </p>

        {/* Die Gabel zum A/B-Test. Kein Blockieren, nur die Information —
            hochgeladene Produkte fallen aus der Testpopulation heraus. */}
        {experimentAtRisk && (
          <div className="flex items-start gap-3 rounded-lg border border-primary/20 bg-accent p-3">
            <FlaskConical className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
            <div className="space-y-1">
              <p className="text-xs font-medium text-primary-ink">{t('experimentFork.title')}</p>
              <p className="text-xs text-primary-ink leading-relaxed">{t('experimentFork.body')}</p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="sm:w-auto">
            {t('cancel')}
          </Button>
          <Button
            onClick={() => onConfirm(addCustomLabel)}
            disabled={countsLoading || approvedCount === 0}
            loading={submitting}
            className="sm:w-auto gap-2"
          >
            <Upload className="h-4 w-4" />
            {t('confirm', { count: approvedCount, formatted: countLabel(approvedCount) })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
